import React from "react";

import Typography from "@mui/material/Typography";

import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import IntegrationDetailHeader from "@app/analytics/Integrations/components/IntegrationDetailHeader";
import IntegrationError from "@app/analytics/Integrations/components/IntegrationError";
import IntegrationNotConnected from "@app/analytics/Integrations/components/IntegrationNotConnected";
import { PROVIDER_CONTENT } from "@app/analytics/Integrations/constants";
import useConnectIntegrationToasts from "@app/analytics/Integrations/hooks/useConnectIntegrationToasts";
import {
  IntegrationDetailContent,
  LoadingIntegrationDetail,
} from "@app/analytics/Integrations/styles";
import { CRM_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import type {
  CrmProviderId,
  Integration,
  IntegrationScope,
} from "@app/common/types";

import HubSpotIntegrationDetails from "./HubSpotIntegrationDetails";
import SalesforceIntegrationDetails from "./SalesforceIntegrationDetails";

interface CrmIntegrationConfigProps {
  providerId: CrmProviderId;
  scope?: IntegrationScope;
}

const CONNECTED_CONTENT_FOR_PROVIDER: {
  [key in CrmProviderId]: React.FunctionComponent<{ integration: Integration }>;
} = {
  hubspot: HubSpotIntegrationDetails,
  salesforce: SalesforceIntegrationDetails,
};

const ConnectedContent = ({ integration }: { integration: Integration }) => {
  const ProviderDetailsComponent =
    CONNECTED_CONTENT_FOR_PROVIDER[integration.providerId as CrmProviderId];

  return (
    <IntegrationDetailContent spacing={3}>
      <ProviderDetailsComponent integration={integration} />
    </IntegrationDetailContent>
  );
};

const CrmIntegrationConfig = ({
  providerId,
  scope = "user",
}: CrmIntegrationConfigProps) => {
  const { isLoading, getIntegration } = useIntegrations();
  const { authorize, disconnect } = useIntegration();

  const providerConfig = CRM_PROVIDERS_BY_ID[providerId];
  const providerContent = PROVIDER_CONTENT[providerId];

  const integration = getIntegration({ providerId, scope });

  useConnectIntegrationToasts({
    integration,
    targetAction: providerConfig.connectAction,
  });

  if (isLoading) {
    return <LoadingIntegrationDetail />;
  }

  if (!integration) {
    return <IntegrationError />;
  }

  const handleClickConnect = () => {
    authorize.mutate({
      providerId: integration.providerId,
      returnRoute: integration.route,
      scope,
    });
  };

  const handleClickDisconnect = () => {
    disconnect.mutate(integration.providerId, {
      onSuccess: () => {
        toast.success(`Disconnected ${integration.displayName}`);
      },
      onError: () => {
        toast.error(
          `There was an error disconnecting ${integration.displayName}. Please try again or contact support@read.ai if the problem persists.`
        );
      },
    });
  };

  const cta = {
    label: "Connect",
    onClick: handleClickConnect,
  };

  return (
    <>
      <IntegrationDetailHeader
        cta={cta}
        provider={integration}
        onClickDisconnect={handleClickDisconnect}
        onReauth={handleClickConnect}
        documentationUrl={integration.documentationUrl}
      >
        <Typography variant="bodySecondaryReg" color="shades.neutral.600">
          {providerContent.headerDescription ??
            providerContent.shortDescription}
        </Typography>
      </IntegrationDetailHeader>
      {integration.isConnected ? (
        <ConnectedContent integration={integration} />
      ) : (
        <IntegrationNotConnected cta={cta} provider={integration} />
      )}
    </>
  );
};

export default CrmIntegrationConfig;
